import React, { useState, useEffect } from "react";
import {
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  RefreshCw,
  ExternalLink,
  FileText,
} from "lucide-react";
import {
  requestCampaignLogs,
  getDownloadTasks,
  deleteDownloadTask,
} from "../apis/downloadCenter";
import { toast } from "react-toastify";

const DownloadCenter = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, in_progress, completed, failed
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadTasks();
    // Auto-refresh every 5 seconds to check for completed tasks
    const interval = setInterval(() => {
      if (filter === "all" || filter === "in_progress") {
        loadTasks(true); // Silent refresh
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [filter]);

  const loadTasks = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const token = localStorage.getItem("userAccessToken");
      const params = {};
      if (filter !== "all") {
        params.status = filter;
      }

      const response = await getDownloadTasks(token, params);
      setTasks(response.data.data || []);
    } catch (error) {
      console.error("Error loading download tasks:", error);
      if (!silent) {
        toast.error("Failed to load download tasks");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this download task?")) {
      return;
    }

    try {
      const token = localStorage.getItem("userAccessToken");
      await deleteDownloadTask(token, taskId);
      toast.success("Download task deleted successfully");
      loadTasks();
    } catch (error) {
      console.error("Error deleting download task:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete download task"
      );
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("File is not ready yet");
      return;
    }

    // Open file in new tab
    window.open(fileUrl, "_blank");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={16} className="text-green-600" />;
      case "failed":
        return <XCircle size={16} className="text-red-600" />;
      case "in_progress":
        return <Clock size={16} className="text-blue-600 animate-spin" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "failed":
        return "bg-red-100 text-red-800 border-red-200";
      case "in_progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const filteredTasks = tasks;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Download Center</h1>
            <p className="text-xs text-gray-600 mt-0.5">
              View and download your campaign log files
            </p>
          </div>
          <button
            onClick={() => loadTasks()}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={refreshing ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === "all"
                ? "bg-[#25D366] text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter("in_progress")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === "in_progress"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === "completed"
                ? "bg-green-600 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setFilter("failed")}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === "failed"
                ? "bg-red-600 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Failed
          </button>
        </div>

        {/* Tasks List */}
        <div className="space-y-3">
          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <p className="text-sm text-gray-500">Loading download tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 font-medium">
                No download tasks found
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {filter !== "all"
                  ? `No ${filter.replace("_", " ")} tasks available`
                  : "Request a campaign log download to get started"}
              </p>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div
                key={task.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-0.5">{getStatusIcon(task.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {task.file_name || "Campaign Logs"}
                        </h3>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${getStatusBadge(
                            task.status
                          )}`}
                        >
                          {task.status.replace("_", " ")}
                        </span>
                      </div>
                      {task.campaign_name && (
                        <p className="text-xs text-gray-600 mb-1">
                          Campaign: {task.campaign_name}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-500">
                        Created:{" "}
                        {new Date(task.created_at).toLocaleString()}
                        {task.updated_at !== task.created_at &&
                          ` | Updated: ${new Date(task.updated_at).toLocaleString()}`}
                      </p>
                      {task.error_message && (
                        <p className="text-xs text-red-600 mt-1">
                          Error: {task.error_message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-2">
                    {task.status === "completed" && task.file_url && (
                      <button
                        onClick={() => handleDownload(task.file_url, task.file_name)}
                        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 border border-green-200"
                        title="Download File"
                      >
                        <Download size={12} />
                        Download
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteTask(task.id)}
                      className="p-1.5 text-red-400 hover:text-red-600"
                      title="Delete Task"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Progress indicator for in_progress */}
                {task.status === "in_progress" && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <Clock size={12} className="text-blue-600 animate-spin" />
                      <p className="text-xs text-gray-600">
                        Generating file... This may take a few moments.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadCenter;

